<template>
  <section class="animated-call-to-action-module-component">
    <div class="inner-container">
      <div
        :id="`text-${uniqueId}`"
        ref="textToBreak"
        class="text"
        v-html="addBGDiv(data.title)" />

      <div
        v-if="data.button"
        class="button-container">
        <ButtonGlobal
          :text="data.button.title"
          :target="data.button.target"
          :url="data.button.url" />
      </div>

      <div
        v-if="data.description"
        class="description-container"
        v-html="data.description" />
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "AnimatedCallToActionComponent",
    "CLASSNAME": "animated-call-to-action-module-component",
    "URL": "http://vuevitewp.local/test"
   }
  #ENDMETA */

/*
 *   Insert your logic here
 */

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import SplitType from 'split-type'
import { onMounted, onBeforeUnmount, ref } from 'vue'
import useUtils from '@/components/composables/useUtils'
import ButtonGlobal from '@/components/utility/ButtonGlobal.vue'

const { genUUID } = useUtils()

gsap.registerPlugin(ScrollTrigger)

const props = defineProps(['data'])
const splitted = ref()
const uniqueId = ref()
const textToBreak = ref()

const animateText = () => {
  gsap.fromTo(
    `#text-${uniqueId.value} div.line > *`,
    {
      y: '100%',
    },
    {
      y: '0%',
      duration: 1,
      stagger: 0.1,
      ease: 'power4.out',
      scrollTrigger: {
        id: `text-${uniqueId.value}`,
        trigger: `#text-${uniqueId.value}`,
        start: 'top-=80% center',
        end: 'bottom-=30% center',
        scrub: 1,
        markers: false,
      },
    }
  )
}

const addBGDiv = (html) => {
  return html.replace(
    /<em>/g,
    `<em><div class="highlight highlight-${uniqueId.value}"></div>`
  )
}

const splitIt = () => {
  const breakIt = document.querySelector(`#text-${uniqueId.value}`)

  if (!breakIt) {
    setTimeout(() => {
      splitIt()
    }, 100)
  } else {
    const textLines = new SplitType(`#text-${uniqueId.value} > *`, {
      types: 'lines, words',
    })
    splitted.value = textLines.lines
    animateText()
  }
}

const changeBgColor = () => {
  const elem = document.querySelector('.site-footer')
  if (!elem) {
    setTimeout(() => {
      changeBgColor()
    }, 100)
  } else {
    gsap.set('.site-footer', {
      background: '#ffffff',
    })
  }
}

onMounted(() => {
  uniqueId.value = genUUID()
  if (props.data.turn_footer_background_color_to_white) {
    changeBgColor()
  }

  splitIt()
})

onBeforeUnmount(() => {
  gsap.set('.site-footer', {
    background: 'transparent',
  })
  const textAnim = ScrollTrigger.getById(`text-${uniqueId.value}`)
  if (textAnim) textAnim.kill()
  const highlightAnim = ScrollTrigger.getById(`highlight-${uniqueId.value}`)
  if (highlightAnim) highlightAnim.kill()
})
</script>

<style lang="scss" scoped>
.animated-call-to-action-module-component {
  @apply bg-white w-full py-8 lg:py-20 px-6 lg:px-0;

  .inner-container {
    @apply mx-auto max-w-[1082px];

    .text:deep() {
      @apply text-center text-[30px] lg:text-[60px] leading-none tracking-normal lg:tracking-[-1.2px];
      * {
        @apply text-[26px] lg:text-[60px] leading-none tracking-normal lg:tracking-[-1.2px];
      }
      p,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        @apply text-balanced;
      }
      .line {
        @apply overflow-hidden text-center;
        text-align: center !important;
      }
      .word {
        @apply text-balanced text-center;
      }
      em {
        @apply not-italic;
        .highlight {
          @apply h-full w-full absolute left-0 bottom-1 z-[-1]
          lg:bottom-[13px] lg:h-[55px];
          background-color: var(--revmed-highlight-color);
        }
      }
    }

    .button-container {
      @apply flex justify-center my-10;
    }

    .description-container {
      @apply text-center;

      &:deep() {
        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        p {
          @apply header-4 text-balanced;
        }

        a {
          @apply underline;

          &:hover {
            @apply cursor-pointer;
          }
        }
      }
    }
  }
}
</style>
